// src/components/events/EventCard.tsx
import { Card, Stack, Box, Typography, Chip } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { EventArticle } from '@/types/news.types';

interface EventCardProps {
  event: EventArticle;
  variant?: 'default' | 'modal';
  onClick?: () => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  };
  return date.toLocaleDateString('es-ES', options);
};

const formatTime = (dateString: string): string => {
  const date = new Date(dateString);

  // Check if time is 12:00am (midnight)
  if (date.getHours() === 0 && date.getMinutes() === 0) {
    return 'Todo el día';
  }

  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  return date.toLocaleTimeString('es-ES', options);
};

export const EventCard = ({ event, variant = 'default', onClick }: EventCardProps) => {
  if (variant === 'modal') {
    return (
      <Card
        elevation={0}
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          p: { xs: 2, md: 3 },
          pointerEvents: 'none'
        }}
      >
        <Chip
          label={event.manager || 'Evento'}
          size="small"
          sx={{
            mb: 2,
            backgroundColor: '#EEF6FF',
            color: '#0B74DE',
            fontWeight: 500,
            fontSize: '0.75rem',
            height: 24,
            alignSelf: 'flex-start'
          }}
        />
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: '#1F2937',
            fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem' }
          }}
        >
          {event.title}
        </Typography>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {formatDate(event.date)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {formatTime(event.date)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {event.place}
            </Typography>
          </Box>
        </Stack>
      </Card>
    );
  }

  // Variant default
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        borderRadius: 2,
        padding: 3,
        width: "90%",
        height: { xs: 300, sm: 210, md: 230, lg: 250 },
        cursor: onClick ? 'pointer' : 'default',
        pointerEvents: 'none'
      }}
    >
      <Chip
        label={event.manager || 'Evento'}
        size="medium"
        sx={{ alignSelf: 'flex-start' }}
      />
      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem' } }}>
        {event.title}
      </Typography>
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            '&:last-child': {
              mb: 0,
            }
          }}
        >
          <CalendarTodayOutlinedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#6b7280', flexShrink: 0 }} />
          <Typography variant="body2">{formatDate(event.date)}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            '&:last-child': {
              mb: 0,
            }
          }}
        >
          <AccessTimeOutlinedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#6b7280', flexShrink: 0 }} />
          <Typography variant="body2">{formatTime(event.date)}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            '&:last-child': {
              mb: 0,
            }
          }}
        >
          <LocationOnOutlinedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#6b7280', flexShrink: 0 }} />
          <Typography variant="body2">{event.place}</Typography>
        </Box>
      </Stack>
    </Card>
  );
};